import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { consolidationErrorText } from '@/components/workorders/consolidation/consolidationError';
import {
  isCollectingInvoice,
  isConsolidationReferenceEditable,
  summarizeVat,
  type InvoiceConsolidation,
} from '@/components/workorders/consolidation/presentation';
import { useNotify } from '@/hooks/useNotify';
import {
  useCloseConsolidatedInvoice,
  useUpdateConsolidationReference,
} from '@/hooks/useConsolidatedInvoices';
import { formatDate } from '@/utils/locale';

interface InvoiceConsolidationSectionProps {
  invoiceId: string;
  status: string;
  canManage: boolean;
  presentation: InvoiceConsolidation | null;
  isError: boolean;
}

function formatPercent(percent: number): string {
  return (Math.round(percent * 100) / 100).toString().replace('.', ',');
}

function CustomerReferenceEditor({
  invoiceId,
  bookingId,
  value,
}: {
  invoiceId: string;
  bookingId: string;
  value: string | null;
}) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const update = useUpdateConsolidationReference();
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const save = async () => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === (value ?? null)) return;
    try {
      await update.mutateAsync({ invoiceId, bookingId, customerReference: next });
      notify.success(t('consolidation.referenceSaved'));
    } catch (error) {
      notify.error(tc('error'), consolidationErrorText(error, t));
    }
  };

  return (
    <TextField
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => { void save(); }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      size="small"
      fullWidth
      disabled={update.isPending}
      slotProps={{
        htmlInput: {
          maxLength: 140,
          'aria-label': t('edit.customerReference'),
          'data-testid': `consolidation-reference-${bookingId}`,
        },
      }}
    />
  );
}

export function InvoiceConsolidationSection({
  invoiceId,
  status,
  canManage,
  presentation,
  isError,
}: InvoiceConsolidationSectionProps) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const closeInvoice = useCloseConsolidatedInvoice();
  const [confirmClose, setConfirmClose] = useState(false);

  if (!presentation) {
    if (!isError) return null;
    return (
      <Alert severity="error" sx={{ mb: 3 }} data-testid="invoice-consolidation-error">
        {t('consolidation.loadError')}
      </Alert>
    );
  }

  const collecting = isCollectingInvoice(status);
  const editable = canManage && isConsolidationReferenceEditable(status);
  const vatRows = summarizeVat(presentation.rows);
  const firstIndexByBooking = new Map<string, number>();
  presentation.rows.forEach((row, index) => {
    if (row.bookingId !== '' && !firstIndexByBooking.has(row.bookingId)) {
      firstIndexByBooking.set(row.bookingId, index);
    }
  });

  const handleClose = async () => {
    try {
      await closeInvoice.mutateAsync({ invoiceId });
      notify.success(t('consolidation.closeSuccess'));
      setConfirmClose(false);
    } catch (error) {
      notify.error(tc('error'), consolidationErrorText(error, t));
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid="invoice-consolidation">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('consolidation.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('consolidation.period', {
              start: formatDate(presentation.periodStart),
              end: formatDate(presentation.periodEnd),
            })}
          </Typography>
        </Box>
        {canManage && collecting ? (
          <Button
            variant="contained"
            data-testid="consolidation-close"
            onClick={() => setConfirmClose(true)}
          >
            {t('consolidation.close')}
          </Button>
        ) : null}
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('consolidation.columns.registration')}</TableCell>
              <TableCell>{t('consolidation.columns.date')}</TableCell>
              <TableCell>{t('consolidation.columns.workOrder')}</TableCell>
              <TableCell>{t('edit.customerReference')}</TableCell>
              <TableCell align="right">{t('consolidation.columns.vat')}</TableCell>
              <TableCell align="right">{t('consolidation.columns.net')}</TableCell>
              <TableCell align="right">{t('consolidation.columns.tax')}</TableCell>
              <TableCell align="right">{t('consolidation.columns.gross')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {presentation.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary' }}>
                  {t('consolidation.empty')}
                </TableCell>
              </TableRow>
            ) : (
              presentation.rows.map((row, index) => {
                const reference = row.customerReference ?? '—';
                const showEditor = editable
                  && row.bookingId !== ''
                  && firstIndexByBooking.get(row.bookingId) === index;
                return (
                  <TableRow key={`${row.bookingId}-${row.taxPercent ?? 'none'}-${index}`} data-testid="consolidation-row">
                    <TableCell>{row.registrationNumber ?? '—'}</TableCell>
                    <TableCell>{formatDate(row.completedDate)}</TableCell>
                    <TableCell>{row.workOrderNumber ?? '—'}</TableCell>
                    <TableCell>
                      {showEditor ? (
                        <CustomerReferenceEditor
                          invoiceId={invoiceId}
                          bookingId={row.bookingId}
                          value={row.customerReference}
                        />
                      ) : (
                        reference
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.taxPercent === null ? '—' : `${formatPercent(row.taxPercent)} %`}
                    </TableCell>
                    <TableCell align="right">{formatPrice(row.netCents)}</TableCell>
                    <TableCell align="right">{formatPrice(row.taxCents)}</TableCell>
                    <TableCell align="right">{formatPrice(row.grossCents)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {vatRows.length > 0 ? (
        <Box sx={{ mt: 2 }} data-testid="consolidation-vat-summary">
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('consolidation.vatSummary')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('consolidation.columns.vat')}</TableCell>
                <TableCell align="right">{t('consolidation.columns.net')}</TableCell>
                <TableCell align="right">{t('consolidation.columns.tax')}</TableCell>
                <TableCell align="right">{t('consolidation.columns.gross')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vatRows.map((row) => (
                <TableRow key={row.taxPercent ?? 'none'} data-testid="consolidation-vat-row">
                  <TableCell>
                    {row.taxPercent === null ? '—' : `${formatPercent(row.taxPercent)} %`}
                  </TableCell>
                  <TableCell align="right">{formatPrice(row.netCents)}</TableCell>
                  <TableCell align="right">{formatPrice(row.taxCents)}</TableCell>
                  <TableCell align="right">{formatPrice(row.grossCents)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      <ConfirmDialog
        open={confirmClose}
        title={t('consolidation.closeTitle')}
        message={t('consolidation.closeConfirm')}
        confirmText={t('consolidation.close')}
        onConfirm={() => { void handleClose(); }}
        onCancel={() => {
          if (!closeInvoice.isPending) setConfirmClose(false);
        }}
        loading={closeInvoice.isPending}
      />
    </Paper>
  );
}
